import React, { type RefAttributes, useCallback, memo, useMemo } from 'react';
import { styled } from '@compiled/react';
import { layers } from '@jira/platform__ui-layers';
import { token } from '@atlaskit/tokens';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { NinChangeboardingTourTarget } from '@atlassian/jira-issue-navigator-changeboarding/src/controllers/enable-nin-changeboarding/index.tsx';
import { cardKeys as spotlightCardKeys } from '@atlassian/jira-issue-navigator-changeboarding/src/controllers/spotlight-coordination/constants.tsx';
import SpotlightTourCard from '@atlassian/jira-issue-navigator-changeboarding/src/ui/spotlight-tour-card/index.tsx';
import SpotlightTourTarget, {
	type Props as SpotlightTourTargetProps,
} from '@atlassian/jira-issue-navigator-changeboarding/src/ui/spotlight-tour-target/index.tsx';
import type {
	AsyncLoadOptions,
	FormatOptionLabel,
	OnChangeWithData,
	SelectValue,
	OnRestoreDefaults,
	OnSortOptions,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import { Picker } from '@atlassian/jira-jql-builder-basic-picker/src/ui/main.tsx';
import type { CustomComponents } from '../../common/types.tsx';
import NoOptionsMessage from '../../common/ui/no-options-message/index.tsx';
import messages from './messages.tsx';
import PickerButton from './picker-button/index.tsx';

export type Props = {
	components?: CustomComponents;
	isOpen: boolean;
	selectedColumns?: SelectValue;
	onLoadOptions: AsyncLoadOptions;
	onChange: OnChangeWithData;
	onRestoreDefaults?: OnRestoreDefaults;
	onOpen: () => void;
	onClose: () => void;
	formatOptionLabel?: FormatOptionLabel;
	isLoading: boolean;
	zIndex?: number;
	isInBetweenColumnPickerEnabled?: boolean;
	columnsDataId?: string;
	showInlineFieldCreateOnboarding?: boolean;
	columnInsertIndex?: number;
	onPickerMouseEnter?: () => void;
	onPickerMouseLeave?: () => void;
	onSortOptions?: OnSortOptions;
};

const ColumnPicker = ({
	components,
	selectedColumns = [],
	isOpen,
	onOpen,
	onClose,
	zIndex,
	isLoading,
	isInBetweenColumnPickerEnabled = false,
	columnsDataId,
	showInlineFieldCreateOnboarding = false,
	columnInsertIndex,
	onPickerMouseEnter,
	onPickerMouseLeave,
	...restProps
}: Props) => {
	const { formatMessage } = useIntl();

	const loadingMessages = useMemo(
		() => ({
			paginationStart: formatMessage(messages.loadingMessageStart),
			paginationEnd: formatMessage(messages.loadingMessageEnd),
		}),
		[formatMessage],
	);

	const onClick = useCallback(() => (isOpen ? onClose() : onOpen()), [isOpen, onClose, onOpen]);
	// Define a CustomNoOptionsMessage component that supports rendering of custom help actions
	const CustomNoOptionsMessage = useCallback(
		() =>
			components?.EmptyState !== undefined ? (
				<components.EmptyState closeColumnPicker={onClose} />
			) : (
				<NoOptionsMessage />
			),
		[components, onClose],
	);
	// Remove all the changeboarding on clean up of 'remove_nin_changeboarding_rll_usages', should be showing as empty fragments already
	const onTrigger = useCallback(
		({ ref }: RefAttributes<HTMLButtonElement>) => {
			const renderSpotlightCard: SpotlightTourTargetProps['renderSpotlightCard'] = ({
				onEndTour,
			}) => (
				<SpotlightTourCard
					cardKey={spotlightCardKeys.GLOBAL_SCOPE_COLUMN_PICKER}
					dialogWidth={272}
					dialogPlacement="left middle"
					actions={[
						{
							onClick: () => {
								onEndTour();
							},
							text: formatMessage(messages.done),
						},
					]}
					heading={formatMessage(messages.tourHeading)}
					target="nin.spotlight.column-picker"
					targetRadius={3}
					targetBgColor={token('elevation.surface')}
					messageId="issue-table-column-picker.ui.column-picker.spotlight-tour-card.nin.spotlight.column-picker.spotlight"
					messageType="transactional"
				>
					{formatMessage(messages.tourBody)}
				</SpotlightTourCard>
			);
			return (
				<SpotlightTourTarget
					name="nin.spotlight.column-picker"
					renderSpotlightCard={renderSpotlightCard}
				>
					<NinChangeboardingTourTarget engagementId="nin.native-issue-table.column-picker">
						<PickerButton
							isInBetweenColumnPickerEnabled={isInBetweenColumnPickerEnabled}
							onClick={onClick}
							ref={ref}
							isSelected={isOpen}
							customIcon={components?.Icon}
							isLoading={isLoading}
							onPickerMouseEnter={onPickerMouseEnter}
							onPickerMouseLeave={onPickerMouseLeave}
						/>
					</NinChangeboardingTourTarget>
				</SpotlightTourTarget>
			);
		},
		[
			isInBetweenColumnPickerEnabled,
			onClick,
			isOpen,
			components?.Icon,
			isLoading,
			formatMessage,
			onPickerMouseEnter,
			onPickerMouseLeave,
		],
	);

	const zIndexChangeEnabled = zIndex ?? layers.modal;

	const pickerComponent = (
		<PickerContainer data-testid="issue-table-column-picker.ui.column-picker.column-picker-container">
			<Picker
				{...restProps}
				fallbackPlacements={
					isInBetweenColumnPickerEnabled && fg('jira_inline_field_config_gate')
						? ['bottom-start']
						: ['left', 'top-end']
				}
				id="column-picker"
				isOpen={isOpen}
				onClose={onClose}
				value={selectedColumns}
				zIndex={zIndexChangeEnabled}
				PopupComponent={components?.PopupComponent}
				placement="bottom-end"
				isInBetweenColumnPickerEnabled={isInBetweenColumnPickerEnabled}
				enableFullList={false}
				onTrigger={onTrigger}
				CustomNoOptionsMessage={CustomNoOptionsMessage}
				searchInputPlaceholder={
					fg('jira_inline_field_config_gate')
						? formatMessage(messages.searchColumnPlaceholderNew)
						: formatMessage(messages.searchColumnPlaceholder)
				}
				// shouldRenderToParent can cause significant issues for the column picker due to a Safari & Firefox
				// bug where overflowing content with fixed positioning inside a sticky container can be cut off –
				// as a mitigation, we need to portal the popup component outside the table boundaries
				// @see https://jplat.jira.atlassian.cloud/browse/EM-7679
				// @see https://stackoverflow.com/q/70939616
				shouldRenderToParent={false}
				CustomFooter={
					// eslint-disable-next-line jira/ff/no-preconditioning
					components?.Footer &&
					fg('jira_inline_field_config_gate') &&
					!fg('slv-117-nin-column-picker-style-updates') ? (
						<components.Footer
							onPickerClose={onClose}
							columnsDataId={columnsDataId}
							columnInsertIndex={columnInsertIndex}
						/>
					) : undefined
				}
				renderFooter={
					// eslint-disable-next-line jira/ff/no-preconditioning
					components?.Footer &&
					fg('jira_inline_field_config_gate') &&
					fg('slv-117-nin-column-picker-style-updates')
						? (rightSideComponent) => {
								return components?.Footer ? (
									<components.Footer
										onPickerClose={onClose}
										columnsDataId={columnsDataId}
										columnInsertIndex={columnInsertIndex}
										rightSideComponent={rightSideComponent}
									/>
								) : null;
							}
						: undefined
				}
				loadingMessages={loadingMessages}
			/>
		</PickerContainer>
	);

	return (showInlineFieldCreateOnboarding || !isInBetweenColumnPickerEnabled) &&
		fg('jira_inline_field_config_gate') ? (
		<SpotlightTarget
			name={`inline-field-create-onboarding-column-picker-button.${isInBetweenColumnPickerEnabled ? 'in-between' : 'default'}`}
		>
			{pickerComponent}
		</SpotlightTarget>
	) : (
		pickerComponent
	);
};

export default memo<Props>(ColumnPicker);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PickerContainer = styled.div({
	fontWeight: token('font.weight.regular'),
	whiteSpace: 'normal',
});
